import { useState } from "react";
import { ConnectionFormDataController } from "../formController";
import { Version } from "../../types/version";
import { Team } from "../../types/team";
import {
  SystemTeamContributionTypes,
  VersionTeamConnection,
} from "../../types/versionTeam";
import useVersionTeamQuery from "../../hooks/query/connections/useVersionTeamQuery";
import { CreateEdgeData } from "../../hooks/query/connections/connectionQueryClient";
import useVersionTeamConnectionLogic from "../../hooks/logic/connections/versionTeamConnectionLogic";
import { extractReferenceKey } from "../../components/forms/fields/basic/EntitySelectionField";

interface VersionTeamController {
  from?: Version | null;
  to?: Team | null;
  versionTeamType: SystemTeamContributionTypes;
  systemLanguageType: VersionTeamConnection["system_language_type"];
}

const useVersionTeamConnectionController = ({
  from = null,
  to = null,
  versionTeamType,
  systemLanguageType,
}: VersionTeamController): ConnectionFormDataController<
  VersionTeamConnection,
  Version,
  Team
> => {
  if ((from === null && to === null) || (from !== null && to !== null)) {
    throw new Error("Exactly one of 'from' or 'to' must be specified");
  }

  const [id, setId] = useState<string | null>(null);
  const direction: "inbound" | "outbound" =
    from === null ? "inbound" : "outbound";
  const anchorEntity: Version | Team = (from || to)!;
  const { useFetchById, useCreate, useUpdate } = useVersionTeamQuery(
    versionTeamType,
    systemLanguageType
  );
  const { defaultVersionTeam, getDisplayName, metadataFieldNames, userEnums } =
    useVersionTeamConnectionLogic(from, to, versionTeamType, systemLanguageType);
  const fetchById = useFetchById(id || "");
  const create = useCreate();
  const update = useUpdate();
  const [isDialogShown, setShowDialog] = useState(false);

  const openCreateDialog = () => {
    setId(null);
    setShowDialog(true);
  };

  const openEditDialog = (id: string) => {
    setId(id);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setId(null);
  };

  const fetchData = async (): Promise<VersionTeamConnection> => {
    if (id === null) {
      return defaultVersionTeam;
    }
    const res = await fetchById.refetch();
    const connectionData = res.data;
    if (!connectionData || res.isError) {
      throw new Error("Team connection not found");
    }
    return connectionData;
  };

  const onSubmit = async (
    data: Partial<VersionTeamConnection>
  ): Promise<VersionTeamConnection> => {
    const metadata: Partial<Omit<VersionTeamConnection, "_from" | "_to">> = {
      name: data.name,
      date: data.date,
      place_id: extractReferenceKey(data.place),
      place_identification_status: data.place_identification_status,
      revision: data.revision?.map((item) => ({
        ...item,
      })),
      title: data.title,
      taisho_catalog_no: data.taisho_catalog_no,
      source_language: data.source_language,
      notes: data.notes,
      internal_notes: data.internal_notes,
      attributes_metadata: {
        ...defaultVersionTeam.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    if (isCreateMode) {
      metadata.system_team_contribution_type = versionTeamType;
      metadata.system_language_type = systemLanguageType;
    }

    const updateData: CreateEdgeData<VersionTeamConnection> = {
      _from: data.fromDoc?._id || from?._id || "",
      _to: data.toDoc?._id || to?._id || "",
      metadata,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...metadata },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update version-team connection");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create version-team connection");
      }
    }

    setId(null);
    return result;
  };

  const isCreateMode = id === null;

  const title = (data: VersionTeamConnection) =>
    `${isCreateMode ? "Edit" : "Add"} ${getDisplayName(data)}`;

  return {
    defaultValues: defaultVersionTeam,
    metadataFieldNames,
    isCreateMode,
    userEnums,
    title,
    fetchData,
    onSubmit,
    formCallback: closeDialog,
    openEditDialog,
    openCreateDialog,
    closeDialog,
    isDialogShown,
    direction,
    anchorEntity,
    from: from || null,
    to: to || null,
  };
};

export default useVersionTeamConnectionController;
